/**
 * Throwing a night away, from wherever the group is looking at it.
 *
 * One component behind four entry points — the draft tile on the home screen,
 * the rating screen itself, the night list on a game's page and the results
 * screen — because the thing being deleted is the same in all four and the
 * control must not drift between them.
 *
 * **One tap, no confirmation.** This used to expand into a panel naming what it
 * destroyed, and it was asked for the other way round: the tap deletes. What is
 * kept from that design is the shape of the trigger — the corner × is small and
 * out of the tap path of the tile it sits on, which is what stops a delete from
 * being reached by accident on a list. Nothing here is undoable, so the cost of
 * this decision is real: `cancelSession` tombstones on the server and there is no
 * put-it-back.
 *
 * **Deleting needs the server**, which is why this is disabled offline instead
 * of queued. The cascade can only be done by the one participant that has seen
 * every rating — on a QR night the guests' answers never reached this device —
 * so there is nothing honest for an offline tap to do. `cancelSession` carries
 * the rest of that reasoning.
 */

import { useState } from "react";
import type { ReactNode } from "react";

import { cancelSession } from "../screens/session/api.ts";
import { useOnline } from "./useOnline.ts";

export interface DeleteNightProps {
  sessionId: string;
  /**
   * `"corner"` on a list row, `"inline"` (the default) on a screen.
   *
   * A list row is already a link to the night, and a labelled button under it
   * competed with that: two targets, one of which was the loud one and the wrong
   * one. So on a row the whole tile resumes and the delete shrinks to an × in
   * the corner, which is small, out of the tap path and conventional enough not
   * to need a word. On the rating and results screens there is no tile to be the
   * link, and a lone × floating over a screen would be a puzzle — those keep the
   * labelled button. `.pb-nightcard` in `styles.css` carries the layout.
   */
  placement?: "inline" | "corner";
  /**
   * Run after the server has accepted the delete. The lists leave it out and
   * simply drop the row; the two screens that are *inside* the deleted night
   * use it to leave.
   */
  onDeleted?: () => void;
  /** Surfaced instead of throwing. The night is gone or it is not. */
  onError?: (message: string) => void;
}

export function DeleteNight(props: DeleteNightProps): ReactNode {
  const online = useOnline();
  // Kept local rather than lifted: three of the four callers are list rows that
  // have nowhere to put an error, and the row itself is where the person who
  // tapped is looking.
  const [failure, setFailure] = useState<string | null>(null);
  // Guards the second tap on a delete already in flight. The row usually
  // disappears under the first one, but a slow server leaves it on screen long
  // enough to be tapped again, and the second request would answer 404 for a
  // night that is being deleted correctly.
  const [busy, setBusy] = useState(false);

  // One wording for all four entry points, the corner × included — it is the
  // glyph's accessible name there, so "Delete" alone would not say what of.
  const name = "Delete this night";
  const corner = props.placement === "corner";
  const disabled = !online || busy;

  const remove = async (): Promise<void> => {
    setFailure(null);
    setBusy(true);
    try {
      await cancelSession(props.sessionId);
      props.onDeleted?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setFailure(message);
      props.onError?.(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={corner ? "pb-delete-night pb-delete-night--corner" : "pb-delete-night"}>
      <button
        type="button"
        className={corner ? "pb-nightcard__x" : "pb-btn"}
        data-variant={corner ? undefined : "quiet"}
        // The glyph carries no name, so the label has to be said out loud —
        // and `title` says it to a mouse as well as to a screen reader.
        aria-label={corner ? name : undefined}
        title={corner ? name : undefined}
        disabled={disabled}
        onClick={() => void remove()}
      >
        {/* U+00D7, not the letter x: it is centred and square. */}
        {corner ? "×" : name}
      </button>
      {!online && (
        <span className="pb-row__hint">Offline — deleting a night needs the server.</span>
      )}
      {failure !== null && (
        <p className="pb-delete-night__error" role="status">
          The night was not deleted: {failure}
        </p>
      )}
    </div>
  );
}
